// FullTestPage.tsx
import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { Box, Button, Typography, CircularProgress, useTheme, Paper, Divider, Chip, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { motion, AnimatePresence } from "framer-motion";
import { UserContext, TestAttempt, SkillName, SkillData, UserData, WeeklyProgressUpdate, applyWeeklyProgressToStudyPlan } from '../UserAndProfile/UserContext';
import { PromptContext, TestState } from '../PromptContext';
import { TestTimer, TestTimerRef } from './TestTimer';
import ChatComponent from '../ChatComponent';
import { useNavigate } from 'react-router-dom';
import TimerIcon from '@mui/icons-material/Timer';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonalizedBar6 from './PersonalizedBar-6';
import { GradeFullTest } from './GradeTest';
import { FullTestProgress } from '../UserAndProfile/UserContext';
import { TestQuestion, UserAnswerRecord } from '../../types';
import SectionTransition from './FullTestSectionTransition';
import { completeFullPracticeTestInPlan } from '../StudyPlan/studyPlanUtils';
import ReadAloudButton from '../Voice/ReadAloudButton';
import { useVoicePlayback } from '../Voice/VoicePlaybackContext';
import { buildQuestionSpeechText } from '../../utils/textForSpeech';


//DONE - you rock :)  ****NOTE TO IVAN ON 3/30/2025: The answer choices are underneath the question, but they should be on teh side.
// Fix this in the morning. 
// 1. Time is negative for some questions - not sure why yet. !
// 2. Could also save every question on handlNextQuestion, but Firestore charges $.18/100,000 writes.
// There's essentially 22/27 questiosn per section, so it costs 22/27 times less, but it a pain for students.
// Probably middle ground is doing an updateUserDoc() every 2-4 questions, but I'll handle that later.*/

// 3. NOT SET UP FOR FREE RESPONSE YET. 
// ALSO, need to implement saving every 2-4 questions instead of once per section.
// 
// Define section types
interface TestSection {
  title: string;
  questions: TestQuestion[];
  timeLimit: number; // in minutes
}

// Define full test structure
interface FullTest {
  sections: TestSection[];
}

const FullTestPage: React.FC = () => {
  // Context
  const { user, updateUserData, saveTestProgress, clearTestProgress } = useContext(UserContext);
  const {clearChat, setQuestionText, setChoices, setCorrectAnswer, setExplanationText, 
    setSelectedSkill, renderLatexOrText, testState, setTestState, startTest, 
    endTest, markQuestion, pictureURL, setPictureURL
  } = useContext(PromptContext)!;
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Helpers (mirror DiagnosticTestPage)
  const skillNameToKey = (skillName: string): string => skillName.toLowerCase().replace(/ /g, '_');

  const createInitialSkillData = (): SkillData => ({
    currentTestQuestionsAttempted: 0,
    currentTestQuestionsCorrect: 0,
    currentTestEasyQuestionsAttempted: 0,
    currentTestEasyQuestionsCorrect: 0,
    easyQuestionsIndex: 0,
    currentTestMediumQuestionsAttempted: 0,
    currentTestMediumQuestionsCorrect: 0,
    mediumQuestionsIndex: 0,
    currentTestHardQuestionsAttempted: 0,
    currentTestHardQuestionsCorrect: 0,
    hardQuestionsIndex: 0,
    overallQuestionsAttempted: 0,
    overallQuestionsCorrect: 0,
  });

  const checkAndUpdatePracticeDate = () => {
    if (!user) return;
    const today = new Date().toISOString().split('T')[0];
    if (user.lastPracticedDate !== today) {
      updateUserData({
        totalDaysPracticed: (user.totalDaysPracticed || 0) + 1,
        lastPracticedDate: today,
      });
    }
  };

  const updateProgressForAnswer = (
    skillDisplayName: string,
    difficulty: 'Easy' | 'Medium' | 'Hard',
    isCorrect: boolean
  ) => {
    if (!user) return;
    const skillKey = skillNameToKey(skillDisplayName);
    const currentSkillData: SkillData = user.skills?.[skillKey] || createInitialSkillData();

    // Build updated skill data by incrementing counters directly
    const updatedSkillData: SkillData = {
      ...currentSkillData,
      overallQuestionsAttempted: (currentSkillData.overallQuestionsAttempted || 0) + 1,
      overallQuestionsCorrect: (currentSkillData.overallQuestionsCorrect || 0) + (isCorrect ? 1 : 0),
      [`currentTest${difficulty}QuestionsAttempted`]:
        ((currentSkillData as any)[`currentTest${difficulty}QuestionsAttempted`] || 0) + 1,
      [`currentTest${difficulty}QuestionsCorrect`]:
        ((currentSkillData as any)[`currentTest${difficulty}QuestionsCorrect`] || 0) + (isCorrect ? 1 : 0),
      easyQuestionsIndex: currentSkillData.easyQuestionsIndex || 0,
      mediumQuestionsIndex: currentSkillData.mediumQuestionsIndex || 0,
      hardQuestionsIndex: currentSkillData.hardQuestionsIndex || 0,
    } as SkillData;

    const userUpdates: Partial<UserData> = {
      skills: { ...(user.skills || {}), [skillKey]: updatedSkillData },
      grandTotalQuestionsAttempted: (user.grandTotalQuestionsAttempted || 0) + 1,
      grandTotalQuestionsCorrect: (user.grandTotalQuestionsCorrect || 0) + (isCorrect ? 1 : 0),
    };

    if (user.studyPlan) {
      const weeklyProgressUpdate: WeeklyProgressUpdate = {
        questionsAttempted: 1,
        questionsCorrect: isCorrect ? 1 : 0,
        activityType: 'test',
        skillUpdates: {
          [skillDisplayName]: {
            attempted: 1,
            correct: isCorrect ? 1 : 0,
            difficulty: difficulty.toLowerCase() as 'easy' | 'medium' | 'hard',
          },
        },
        studyMinutes: 1.5, // Estimate 90 seconds per question
      };
      
      userUpdates.studyPlan = applyWeeklyProgressToStudyPlan(user.studyPlan, weeklyProgressUpdate);
    }

    // Apply all updates in a single Firestore write
    updateUserData(userUpdates);
  };

  // State for test data and UI
  const [questions, setQuestions] = useState<TestQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fullTest, setFullTest] = useState<FullTest | null>(null);
  
  // State for test selection
  const [selectedTestNumber, setSelectedTestNumber] = useState<number>(1);
  const [currentSection, setCurrentSection] = useState<number>(0);
  const [showSectionTransition, setShowSectionTransition] = useState<boolean>(false);
  const [lastQuestionTimestamp, setLastQuestionTimestamp] = useState<number>(0);
  
  
  // State for current question
  const [currentQuestion, setCurrentQuestion] = useState<TestQuestion | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(false);
  const [incorrectAnswers, setIncorrectAnswers] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<string>('');
  const [hasSubmittedFirstAttempt, setHasSubmittedFirstAttempt] = useState(false);
  

  
  // Timer reference
  const timerRef = useRef<TestTimerRef>(null);
  
  const [hasResumed, setHasResumed] = useState(false); // Flag to ensure resume runs only once
  const { stop: stopVoicePlayback } = useVoicePlayback();

  const questionSpeechText = useMemo(
    () => (currentQuestion ? buildQuestionSpeechText(currentQuestion) : ''),
    [currentQuestion]
  );

  useEffect(() => {
    stopVoicePlayback();
  }, [testState.currentQuestionIndex, currentSection, stopVoicePlayback]);

  // Load full test questions
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await fetch(`/satquestions/full-test-${selectedTestNumber}.json`);
        if (!response.ok) {
          throw new Error(`Failed to load full test ${selectedTestNumber}`);
        }
        
        const data = await response.json();
        setFullTest(data);
        
        // Set questions for the current section
        if (data.sections && data.sections.length > currentSection) {
          setQuestions(data.sections[currentSection].questions);
        }
        
        setLoading(false);
      } catch (err) {
        setError('Error loading full test: ' + (err instanceof Error ? err.message : String(err)));
        setLoading(false);
      }
    };
    
    fetchQuestions();
  }, [selectedTestNumber]);
  
  // Load section questions when section changes
  useEffect(() => {
    if (fullTest && fullTest.sections && fullTest.sections.length > currentSection) {
      setQuestions(fullTest.sections[currentSection].questions);
      
      // Previously we reset currentQuestionIndex and cleared answers/marks here.
      // That caused resumed progress to be lost on initial load.
      // Clearing is handled explicitly when moving to a new section via handleNextSection.
    }
  }, [currentSection, fullTest]);
  
  // Set current question when index changes
  //With so many useStates in the dependency, does this reduce performance?
  useEffect(() => {
    if (questions.length > 0 && testState.isActive) {
      const question = questions[testState.currentQuestionIndex];
      setCurrentQuestion(question);

      // Reset previous question state
      setSelectedAnswer('');
      setIsAnswerSubmitted(false);
      setIncorrectAnswers([]);
      setHasSubmittedFirstAttempt(false);
      setFeedback('');
      
      // Update PromptContext for ChatComponent
      setQuestionText(question.question);
      setChoices(question.choices);
      setCorrectAnswer(question.answer);
      setExplanationText(question.explanation);
      setSelectedSkill(question.skill);
      if (question.pictureURL) {
        console.log("Setting pictureURL in FullTestPage:", question.pictureURL);
        setPictureURL(question.pictureURL);
      }
    }
  }, [testState.currentQuestionIndex, questions, testState.isActive, setQuestionText, setChoices, setCorrectAnswer, setExplanationText, setSelectedSkill]);
  

  // *** MODIFIED useEffect FOR RESUMING TEST ***
  useEffect(() => {
    console.log("Resume useEffect triggered. HasResumed:", hasResumed, "Loading:", loading, "User Progress:", !!user?.fullTestProgress, "FullTest:", !!fullTest);

    // --- MODIFIED CONDITIONS ---
    // Only run if we haven't already resumed, data is loaded, and there's progress to resume
    if (hasResumed || loading || !user?.fullTestProgress || !fullTest) {
      // If loading is finished and there's no progress, ensure we don't think we are resuming
      if (!loading && !user?.fullTestProgress) {
          setHasResumed(true); // Mark as "resumed" (or rather, initial state determined)
      }
      return; // Exit if already resumed, still loading, or no progress/test data
    }


    const savedProgress = user.fullTestProgress;
    console.log("Found saved progress:", savedProgress);

    // Resume the exact saved section (do NOT auto-advance)
    // If savedProgress.currentSection is undefined, default to 0.
    const sectionToResume = (savedProgress.currentSection ?? 0);

    console.log("Calculated sectionToResume:", sectionToResume);

    // Check if the test was actually completed
    if (sectionToResume >= fullTest.sections.length) {
      console.log("Saved progress indicates test was already completed.");
      // Optionally navigate to results or handle appropriately
      clearTestProgress('fullTest'); // Maybe clear progress if test is done?
      setHasResumed(true); // Mark as handled
      return;
    }

    // Check if the section to resume exists in saved progress (it should unless starting section 0)
    const currentSectionProgress = savedProgress.sections?.[sectionToResume];
    const sectionQuestions = fullTest.sections[sectionToResume]?.questions || [];

    if (!currentSectionProgress && sectionToResume > 0) {
        console.warn(`Attempting to resume section ${sectionToResume}, but no progress found for it. Starting section fresh.`);
        // Proceed to set state for a fresh start of sectionToResume
    } else {
        console.log(`Resuming section ${sectionToResume} with progress:`, currentSectionProgress);
    }
    console.log(`Found saved progress for Test ${savedProgress.testNumber}, Section ${sectionToResume + 1}. Resuming...`);

    const resumedElapsedTime = currentSectionProgress?.elapsedTime || 0;

    // 1. Update component state
    setSelectedTestNumber(savedProgress.testNumber);
    setCurrentSection(sectionToResume); // Set the current section state

    // 2. Update PromptContext's testState
    setTestState({
      type: 'fullTest',
      isActive: true, // Start the resumed section as active
      testNumber: savedProgress.testNumber,
      currentSection: sectionToResume,
      // Use saved progress if available, otherwise defaults for a fresh section start
      currentQuestionIndex: currentSectionProgress?.currentQuestionIndex || 0,
      answers: currentSectionProgress?.answers || [],
      markedQuestions: currentSectionProgress?.markedQuestions || [],
      totalQuestions: sectionQuestions.length,
      // Approximate original start time based on elapsed time
      startTime: Date.now() - resumedElapsedTime,
      elapsedTime: resumedElapsedTime,
    });

    setLastQuestionTimestamp(resumedElapsedTime); // Set timestamp for time tracking

    // --- Mark as resumed ---
    setHasResumed(true);
  }, [user, fullTest, loading, setTestState, setSelectedTestNumber, setCurrentSection, hasResumed, clearTestProgress]);
  // --- END MODIFIED DEPENDENCIES ---


  // Start the full test
  const handleStartTest = useCallback(() => {
    if (fullTest && fullTest.sections && fullTest.sections.length > 0) {
      clearChat();
      setCurrentSection(0);
      startTest('fullTest', fullTest.sections[0].questions.length);
      
      
      // Set test number in test state
      setTestState(prev => ({
        ...prev,
        testNumber: selectedTestNumber,
        currentSection: 0
      }));

      setLastQuestionTimestamp(0);
      checkAndUpdatePracticeDate();

    }
    else {
      console.error('No questions found for full test.');
    }
  }, [fullTest, startTest, clearChat, selectedTestNumber, setTestState]);
  
  // Handle answer selection
  const handleAnswerSelect = (answer: string) => {
    if (!isAnswerSubmitted) { //This is to prevent the user from selecting an answer after the first attempt.
      setSelectedAnswer(answer);
    }
  };

  
  // Handle moving to next question
  const handleNextQuestion = () => { //Should still be called even on last question and it should handle it.
    let timeSpentOnQuestion = 0;
    const currentTime = timerRef.current?.getElapsedTime() || 0;
    timeSpentOnQuestion = currentTime - lastQuestionTimestamp;
   
    // If an answer was selected, record it
    if (selectedAnswer && currentQuestion) {
        const selectedLetter = selectedAnswer[0];
        const correctLetter = currentQuestion.answer[0];
        const isCorrect = selectedLetter === correctLetter;

        const existingIdx = testState.answers.findIndex(a => a.questionIndex === testState.currentQuestionIndex);
        let nextAnswers: UserAnswerRecord[];
        if (existingIdx >= 0) {
          const prev = testState.answers[existingIdx];
          const accumulatedTime = (prev.timeSpent || 0) + timeSpentOnQuestion;
          const updated: UserAnswerRecord = {
            ...prev,
            selectedAnswer: selectedAnswer,
            correctAnswer: correctLetter,
            isCorrect,
            timeSpent: accumulatedTime,
            skill: currentQuestion.skill,
            difficulty: currentQuestion.difficulty,
          };
          nextAnswers = testState.answers.map((a, i) => (i === existingIdx ? updated : a));
        } else {
          const newAnswer: UserAnswerRecord = {
            questionIndex: testState.currentQuestionIndex,
            selectedAnswer: selectedAnswer,
            correctAnswer: correctLetter,
            isCorrect: isCorrect,
            skill: currentQuestion.skill,
            difficulty: currentQuestion.difficulty,
            timeSpent: timeSpentOnQuestion
          };
          nextAnswers = [...testState.answers, newAnswer];
        }

        setTestState((prev: TestState) => ({
            ...prev,
            answers: nextAnswers
        }));

        // Update progress for every submission (as requested)
        updateProgressForAnswer(currentQuestion.skill, currentQuestion.difficulty as 'Easy' | 'Medium' | 'Hard', isCorrect);

        //Increments to the next question if it's not the last question.
        //******NOTE TO FUTURE IVAN from 4/8/2025: 
        // Add logic here to save every 2-4 questions instead of once per section so users don't lose so much progress. */
        if (testState.currentQuestionIndex < questions.length - 1) {
            setTestState((prev: TestState) => ({
                ...prev,
                currentQuestionIndex: prev.currentQuestionIndex + 1
            }));

            // Reset for the next question
            setSelectedAnswer('');
            setIsAnswerSubmitted(false);
        } else {
            // This is the last question in the section, so save the progress.
            handleSectionComplete(nextAnswers);
        }

      // Update the last question timestamp
      setLastQuestionTimestamp(currentTime);
    } else {// Handle case where no answer is selected (though button should be disabled)
      // If moving to next question without answering, still update timestamp
      setLastQuestionTimestamp(currentTime);
      if (testState.currentQuestionIndex < questions.length - 1) {
        // Move to next question index without saving an answer
        setTestState(prev => ({ ...prev, currentQuestionIndex: prev.currentQuestionIndex + 1 }));
      } else {
        // Handle moving to next section or finishing test without answering last question
        handleSectionComplete([]); // Pass empty array or handle appropriately
      }
    }

    
  };
  
  // Handle navigation to previous question
  const handlePreviousQuestion = () => {
    if (testState.currentQuestionIndex > 0) {
      setTestState((prev: TestState) => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex - 1
      }));
    }
  };
  
  // Toggle marking a question for review
  const toggleMarkQuestion = () => {
    markQuestion(testState.currentQuestionIndex);
  };
  
  // Handle direct navigation to a specific question
  const handleQuestionSelect = (index: number) => {
    setTestState((prev: TestState) => ({
      ...prev,
      currentQuestionIndex: index
    }));
  };
  
  // Handle section completion
  const handleSectionComplete = (newAnswers: UserAnswerRecord[]) => {
    console.log("Completing section:", currentSection);
    console.log("Test state answers AT START of handleSectionComplete:", testState.answers);
    // Save progress for current section
    if (user && fullTest) {
      const totalElapsedTime = timerRef?.current?.getElapsedTime() || 0;
      const existingProgress = user.fullTestProgress || { testNumber: selectedTestNumber, currentSection: -1, sections: [] };       // Create a copy of the current sections or initialize if not exists (as backup, but it's an error if it happens)
      const sections = [...existingProgress.sections];
      console.log("Existing sections:", sections);
      const sectionToUpdateIndex = currentSection; //This is the local state variable.
      console.log("Section to update:", sectionToUpdateIndex);
      
      //Using newAnswers instead of testState.answers because the useState for testState.answers is 
      // asynchronous and doesn't update fast enough to be used here, so we use the newAnswers array that was passed in.
      const answersCopy = JSON.parse(JSON.stringify(newAnswers));
      console.log(`Copying ${answersCopy.length} answers for section index ${sectionToUpdateIndex}:`, answersCopy);

      // Ensure the sections array is long enough
      while (sections.length <= sectionToUpdateIndex) {
        sections.push({ answers: [], markedQuestions: [], currentQuestionIndex: 0, elapsedTime: 0, startTime: 0 }); // Add placeholder if needed
      }
      
      sections[sectionToUpdateIndex] = {
        currentQuestionIndex: testState.currentQuestionIndex,
        answers: answersCopy, // Use the deep copy
        markedQuestions: testState.markedQuestions,
        elapsedTime: totalElapsedTime,
        startTime: testState.startTime
      };
      console.log("Updated section with answers:", sections[sectionToUpdateIndex]);
      
      const progress: FullTestProgress = {
        testNumber: selectedTestNumber,
        currentSection: currentSection,
        sections: sections
      };
      console.log("Saving progress:", progress);
      saveTestProgress('fullTest', progress);

      // Stop the timer
      setTestState(prev => ({ ...prev, isActive: false }));
      
      // Check if there are more sections
      if (fullTest && currentSection < fullTest.sections.length - 1) {
        // Show section transition screen
        setShowSectionTransition(true);
      } else {
        // End of test
        handleFinishTest(progress);
      }

    } else {
      console.error("User data not available in handleSectionComplete.")
    }
    
    
  };
  
  // Handle moving to next section
  const handleNextSection = () => {
    const nextSectionIndex = currentSection + 1; // Calculate the target index rather than using prev currentSection + 1 which woudl update asynchronously and potentially not in time to be used within this same function.
    console.log("Moving to next section from", currentSection, "to", nextSectionIndex);
    console.log("Current test state BEFORE update:", testState);

    setShowSectionTransition(false);
    setCurrentSection(nextSectionIndex); // Update the section state

    // Update test state for the *new* section
    setTestState(prev => {
      console.log("Updating test state for new section, previous state:", prev);
      // Ensure we use the freshly calculated nextSectionIndex, not a potentially stale value still being updated asynchronously.
      return {
        ...prev,
        currentQuestionIndex: 0, // Reset question index
        answers: [], // Clear answers
        markedQuestions: [], // Clear marks
        elapsedTime: 0, // Reset timer
        startTime: Date.now(), // Set new start time
        currentSection: nextSectionIndex, // Set correct section index in testState
        isActive: true // Make the new section active
      };
    });

    setLastQuestionTimestamp(0); // Reset timestamp for time tracking

  };
  
  // Finish the test and save results ***
  
  const handleFinishTest = (finalProgress: FullTestProgress | null) => {
    console.log("handleFinishTest called with final progress:", finalProgress);
    // End the test in PromptContext by setting this TestAttempt.isActive to false
    endTest();
    
    // Calculate scores and create test attempt record
    if (user && fullTest && finalProgress?.sections) {
      
      /*Create skill performance summary ********************* Note to future Ivan from 4/8/2025:
      This currently isn't used but could be usedlater for 
        reporting on skills in FullTestResultsPage.tsx
      
        // Collect all answers from all sections
      const allAnswers = testState.answers;
      const totalQuestions = questions.length;
      const skillPerformance: Record<string, { total: number; correct: number }> = {};
      
      allAnswers.forEach(answer => {
        if (!skillPerformance[answer.skill]) {
          skillPerformance[answer.skill] = { total: 0, correct: 0 };
        }
        
        skillPerformance[answer.skill].total++;
        if (answer.isCorrect) {
          skillPerformance[answer.skill].correct++;
        }
      }); */
      
      // Create test attempt record
        let testAttempt: TestAttempt = {
          id: `full-test-${selectedTestNumber}-${Date.now()}`,
          testType: 'full',
          testNumber: selectedTestNumber,
          date: new Date().toISOString(),
          completed: true,
          // Map over the sections from the finalProgress object
          sections: finalProgress.sections.map((sectionProgress, idx) => ({
            sectionNumber: idx + 1,
            // The answers are already UserAnswerRecord[], matching the target type
            questions: sectionProgress.answers, // Direct assignment
            elapsedTime: sectionProgress.elapsedTime || 0
          }))
          // Scores added after grading
        };

        // Calculate scores using the utility function, passing the testAttempt directly to GradeFullTest
        const gradedFullTestScores = GradeFullTest(testAttempt);
        const readingWritingScore = gradedFullTestScores.readingWritingScore;
        const mathScore = gradedFullTestScores.mathScore;
        const totalScore = gradedFullTestScores.totalScore;

        // Update the testAttempt with the calculated scores
        testAttempt.readingWritingScore = readingWritingScore;
        testAttempt.mathScore = mathScore;
        testAttempt.totalScore = totalScore;
        
        const userUpdates: Partial<UserData> = {
          testAttempts: [...(user.testAttempts || []), testAttempt],
          fullTestProgress: null,
        };
        if (user.studyPlan) {
          userUpdates.studyPlan = completeFullPracticeTestInPlan(user.studyPlan);
        }
        updateUserData(userUpdates);

        console.log('Test attempt saved: ', testAttempt);
        
        // Navigate to results page
        navigate('/full-test-results');
      
      
    } else {
      console.error('No full test progress found for user or missing data in handleFinishTest.');
      // Do I want to clear any potentially lingering progress just in case?
      /*if (user) {
        clearTestProgress('fullTest');
      }*/
      navigate('/sat'); // Navigate back to SAT page as a fallback
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <Typography color="error" variant="h6">{error}</Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>Retry</Button>
      </Box>
    );
  }
  
  // Show section transition screen
  if (showSectionTransition) {
    return <SectionTransition
      currentSection={currentSection}
      nextSection={handleNextSection}
    />;
  }
  
  // Render active test
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {!testState.isActive ? (
        <Box sx={{ p: 3, maxWidth: '800px', mx: 'auto' }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/sat')}>
              Back to Dashboard
            </Button>
          </Box>
          <Typography variant="h4" sx={{ mb: 3 }}>SAT Full Practice Test</Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This full-length SAT practice test simulates the actual test experience.
            The test contains 4 sections and will take 2 hours and 14 minutes to complete, plus a 10 minute break between the Reading and Writing and the Math sections.
          </Typography>
          
          {/* Test selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Select a Practice Test:</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {[1, 2].map(num => (//, 3, 4 //Add 3 and 4 back to the array once they're finished and available.
                <Button key={num} variant={selectedTestNumber === num ? "contained" : "outlined"}
                  onClick={() => setSelectedTestNumber(num)}
                  /*disabled={num > 1} //***************REMOVE THIS ONCE OTHER TESTS ARE AVAILABLE! */
                >
                  Test {num}
                </Button>
              ))}
            </Box>
          </Box>
          
          <Button variant="contained" color="primary" size="large" onClick={handleStartTest}>
            Start Full Practice Test
          </Button>
        </Box>
      ) : (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <PersonalizedBar6 
            title={`${!isMobile ? `Test ${selectedTestNumber} - ` : ''}${currentSection + 1 > 2 ? 'Math' : 'Reading & Writing'} Section ${currentSection + 1 > 2 ? currentSection + 1 - 2 : currentSection + 1}`}
            currentIndex={testState.currentQuestionIndex} 
            totalItems={questions.length} 
            timerRef={timerRef}
            currentAnswers={testState.answers.map(a => ({
              questionIndex: a.questionIndex,
              answer: a.selectedAnswer,
              isCorrect: a.isCorrect
            }))}
            markedQuestions={testState.markedQuestions}
            onNavigate={handleQuestionSelect}
            showResults={!testState.isActive && testState.answers.length > 0}
          />
          
          <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', gap: 0, paddingX: '3vw'}}>
            {/* Current question with enhanced styling */}
            <AnimatePresence mode="wait">
              {currentQuestion && (
                <motion.div
                  key={testState.currentQuestionIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box sx={{flex: 1, overflow: 'auto', bgcolor: 'background.paper', px: {xs: 1, md: 3}, py: {xs: 2, md: 3}, borderRadius: 2, 
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', border: '1px solid', borderColor: 'divider', mb: 2}}>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, position: 'relative'}}>
                      {/* Top row with question number */}
                      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, pr: { xs: 0, sm: 12 }}}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          Question {testState.currentQuestionIndex + 1} of {questions.length}
                        </Typography>
                        {questionSpeechText && (
                          <ReadAloudButton
                            id={`fulltest-q-${currentSection}-${testState.currentQuestionIndex}`}
                            text={questionSpeechText}
                            alreadyNormalized
                            ariaLabel="Read question and choices aloud"
                          />
                        )}
                      </Box>
                      
                      {/* Skill and difficulty badges - absolutely positioned */}
                      <Box sx={{position: 'absolute', top: -8, right: 0, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end', zIndex: 1}}>
                        <Chip label={currentQuestion.skill} color="primary" variant="outlined" size="small"/>
                        <Chip label={currentQuestion.difficulty} color={currentQuestion.difficulty === 'Easy' ? 'success' : currentQuestion.difficulty === 'Medium' ? 'warning' : 'error'} variant="outlined" size="small"/>
                      </Box>
                      
                      {/* Main content area with question and answers side by side */}
                      <Box sx={{display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: {xs: 0, md: 3}, mt: 2}}>
                        {/* Question text on the left */}
                        <Box sx={{flex: '3 1 0', minWidth: '300px'}}>
                          {currentQuestion.pictureURL && (
                            <Box sx={{ mb: 2, textAlign: 'center' }}>
                              <img src={currentQuestion.pictureURL}
                                alt={`Diagram for question ${testState.currentQuestionIndex + 1}`}
                                style={{ maxWidth: '100%', maxHeight: '300px', height: 'auto', display: 'block', margin: '0 auto', borderRadius: '4px', border: `1px solid ${theme.palette.divider}` }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  console.warn(`Failed to load image: ${currentQuestion.pictureURL}`);
                                }}
                              />
                            </Box>
                          )}
                          {renderLatexOrText(currentQuestion.question, 'normal')}
                        </Box>
                        
                        {/* Answer choices on the right */}
                        <Box sx={{flex: '2 1 0', minWidth: '250px'}}>
                          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                            Select your answer:
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {currentQuestion.choices.map((choice, index) => {
                              const choiceLetter = String.fromCharCode(65 + index);
                              // Check if this choice is the currently selected one
                              const isSelected = selectedAnswer === choiceLetter; // Use choiceLetter here

                              return (
                                <Button
                                  key={index}
                                  variant="outlined" // Base variant is outlined
                                  onClick={() => handleAnswerSelect(choiceLetter)} // Use choiceLetter
                                  // disabled={isAnswerSubmitted} // Not needed here as feedback isn't immediate
                                  fullWidth // Ensure buttons take full width of their container
                                  sx={{
                                    p: 1.5, // Match padding
                                    borderRadius: 2, // Match border radius
                                    border: 2, // Match border width
                                    justifyContent: 'flex-start', // Align content left
                                    textAlign: 'left',
                                    textTransform: 'none', // Prevent uppercase text
                                    transition: 'all 0.2s ease',

                                    // --- Dynamic Styling based on selection ---
                                    borderColor: isSelected ? 'primary.main' : 'divider', // Highlight border if selected
                                    bgcolor: isSelected ? theme.palette.custom.veryLightOrange : 'background.paper', // Use theme color if selected
                                    color: 'text.primary', // Ensure text is readable

                                    // --- Hover Styling ---
                                    '&:hover': {
                                      borderColor: isSelected ? 'primary.main' : 'grey.400', // Keep border or slightly darken
                                      // Use specific hover colors from DiagnosticTest
                                      bgcolor: isSelected
                                                ? 'rgba(241, 101, 34, 0.12)' // Darker orange hover if selected
                                                : 'rgba(0, 0, 0, 0.04)', // Light gray hover otherwise
                                      color: 'text.primary',
                                      transform: 'scale(1.01)',
                                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                      // Ensure background color doesn't revert on hover if selected
                                      ...(isSelected && { bgcolor: 'rgba(241, 101, 34, 0.12)' })
                                    },
                                  }}
                                >
                                  {/* Keep existing structure for letter + choice text */}
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                    {/*<Typography sx={{ mr: 2, minWidth: '24px', fontWeight: 'medium' }}>
                                      {choiceLetter}.
                                    </Typography>*/}
                                    <Box>
                                      {renderLatexOrText(choice, 'normal', true)}
                                    </Box>
                                  </Box>
                                </Button>
                              );
                            })}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    
                    {/* Navigation buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, gap: 1 }}>
                      <Button variant="outlined" sx={{fontSize: {xs: '.9rem', md: '1.2 rem'}}} startIcon={<ArrowBackIcon />}
                        onClick={handlePreviousQuestion}
                        disabled={testState.currentQuestionIndex === 0}
                      >
                      Previous
                    </Button>
                      <Button variant="outlined" sx={{fontSize: {xs: '.9rem', md: '1.2 rem'}}} color="secondary" 
                        onClick={toggleMarkQuestion}>
                          {testState.markedQuestions.includes(testState.currentQuestionIndex) 
                          ? 'Unmark Question' 
                          : 'Mark for Review'}
                      </Button>
                    
                      <Button variant="contained" sx={{fontSize: {xs: '.9rem', md: '1.2 rem'}}} color="primary" 
                      onClick={handleNextQuestion} endIcon={<SkipNextIcon />}>
                          {testState.currentQuestionIndex === questions.length - 1 
                              ? currentSection < (fullTest?.sections.length || 0) - 1
                                ? 'Next Section'
                                : 'Finish Test' 
                              : 'Next Question'}
                      </Button>
                    </Box>
                  </Box>

                  <Box sx={{mt: 2}}>
                      <ChatComponent />
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FullTestPage;